import { useNavigate } from "react-router-dom";
import { useSuspenseQuery } from "@tanstack/react-query";
import { getExpertAssesmentStats } from "../../services/ipExpertService";
import { useUpdateCalendlyLink } from "../../services/ipExpertMutations";
import { useState } from "react";
import Button from "../../Components/Button";
import { inputClasses } from "../../Components/Form/InputField";
import { daysAgo } from "../../utilities/helperFunctions";
import { DataTable } from "../../Components/ui/data-table";
import { type ColumnDef } from "@tanstack/react-table";
import { Badge } from "../../Components/ui/badge";
import { Card, CardContent } from "../../Components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../Components/ui/tooltip";
import { detectMeetingProvider } from "../../utilities/meetingLinkUtils";
import { ensureHttps } from "@eip/shared/utils/url";
import {
  ExternalLink,
  Pencil,
  Plus,
  AlertTriangle,
  Clock,
  CheckCircle2,
  FileCheck,
  Users,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { queryKeys } from "../../utilities/queryKeys";
import { IPE_STATUS_GROUPS, IPE_FOCUS_STATUSES } from "../../utilities/ipeStatusGroups";
import { format } from "date-fns";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface IpExpertDashboardRow {
  appId: string;
  companyName: string;
  actionItem: string;
  lastUpdated: string;
  contactPerson: string;
  stageChangedAt?: string;
  daysInStage: string | number;
}

interface StatCardProps {
  label: string;
  value: number;
  icon: LucideIcon;
  tier: "urgent" | "attention" | "info" | "progress" | "neutral";
  onClick?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STATUS_BADGE_VARIANT: Record<string, "warning" | "secondary" | "default" | "success" | "destructive" | "outline"> = {
  "New Assessment Request": "warning",
  "Ongoing Assessment": "secondary",
  "SoW Awaiting Approval": "warning",
  "Submitted Assessment": "default",
  "Board Review": "default",
  "SoW & Project": "secondary",
  "SoW requested": "secondary",
  "Rejected": "destructive",
  "Complete": "success",
  "Invite Sent": "outline",
};

const TIER_STYLES: Record<string, { bg: string; iconColor: string }> = {
  urgent: { bg: "bg-cDenied", iconColor: "text-cError" },
  attention: { bg: "bg-cLightYellow", iconColor: "text-amber-600" },
  info: { bg: "bg-cLightGreen", iconColor: "text-cSuccess" },
  progress: { bg: "bg-blue-100", iconColor: "text-blue-600" },
  neutral: { bg: "bg-cLightGrey", iconColor: "text-muted-foreground" },
};


const columns: ColumnDef<IpExpertDashboardRow, unknown>[] = [
  {
    accessorKey: "companyName",
    header: "Startup",
  },
  {
    accessorKey: "actionItem",
    header: "Status",
    cell: ({ getValue }) => {
      const status = String(getValue());
      return <Badge variant={STATUS_BADGE_VARIANT[status] ?? "outline"}>{status}</Badge>;
    },
  },
  {
    accessorKey: "lastUpdated",
    header: "Last Updated",
    cell: ({ getValue }) => {
      const date = getValue();
      return date ? format(new Date(String(date)), "MMM do yyyy") : "-";
    },
  },
  {
    accessorKey: "contactPerson",
    header: "Founder",
  },
  {
    accessorKey: "daysInStage",
    header: "Days in Stage",
  },
];

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function StatCard({ label, value, icon: Icon, tier, onClick }: StatCardProps) {
  const style = TIER_STYLES[tier];
  const isActive = value > 0;
  const clickable = !!onClick;

  return (
    <Card
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick!(); } } : undefined}
      className={`${isActive ? style.bg : ""} ${clickable ? "cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-cPrimaryGreen" : ""}`}
    >
      <CardContent className="p-4 flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${isActive ? style.iconColor : "text-muted-foreground"}`} />
          <span className="text-2xl font-bold">{value}</span>
        </div>
        <p className="text-xs text-muted-foreground text-center leading-tight">{label}</p>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Meeting Link Card (shared between both states)                     */
/* ------------------------------------------------------------------ */

function MeetingLinkCard({
  currentLink,
  updatingCalendly,
  setUpdatingCalendly,
  inputValueCalendly,
  setInputValueCalendly,
  isSaving,
  onSave,
}: {
  currentLink: string;
  updatingCalendly: boolean;
  setUpdatingCalendly: (v: boolean) => void;
  inputValueCalendly: string;
  setInputValueCalendly: (v: string) => void;
  isSaving: boolean;
  onSave: () => void;
}) {
  const provider = detectMeetingProvider(updatingCalendly ? inputValueCalendly : currentLink);
  const ProviderIcon = provider.icon;

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        {updatingCalendly ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-text">Update Meeting Link</p>
              {inputValueCalendly.trim() && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <ProviderIcon className="h-3 w-3" />
                  {provider.name}
                </Badge>
              )}
            </div>
            <input
              type="text"
              value={inputValueCalendly}
              onChange={(e) => setInputValueCalendly(e.target.value)}
              placeholder="https://calendly.com/your-name/meeting"
              className={`${inputClasses} !inline-block w-full`}
            />
            <div className="flex gap-2">
              <Button
                buttonText={isSaving ? "Saving..." : "Save"}
                actionHandler={onSave}
              />
              <Button
                buttonText="Cancel"
                actionHandler={() => {
                  setUpdatingCalendly(false);
                  setInputValueCalendly("");
                }}
                buttonType="secondary"
              />
            </div>
          </div>
        ) : currentLink ? (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-lg bg-cSage flex items-center justify-center shrink-0">
                <ProviderIcon className="h-4 w-4 text-cGreen" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-text">Meeting Link</p>
                  <Badge variant="secondary">{provider.name}</Badge>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        href={ensureHttps(currentLink)}
                        target="_blank"
                        className="text-sm text-cSuccess hover:underline truncate block max-w-md"
                      >
                        {currentLink}
                      </a>
                    </TooltipTrigger>
                    <TooltipContent><p>{currentLink}</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={ensureHttps(currentLink)}
                target="_blank"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-borders text-text/70 hover:bg-gray-50 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                Test Link
              </a>
              <button
                onClick={() => {
                  setInputValueCalendly(currentLink);
                  setUpdatingCalendly(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-borders text-text/70 hover:bg-gray-50 transition-colors"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center">
                <Plus className="h-4 w-4 text-text/40" />
              </div>
              <div>
                <p className="text-sm font-medium text-text">No meeting link set</p>
                <p className="text-xs text-text/50">Add your scheduling link so founders can book meetings with you</p>
              </div>
            </div>
            <Button
              buttonText="Add Link"
              actionHandler={() => setUpdatingCalendly(true)}
              size="small"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

const IpExpertDashboard = () => {
  const [updatingCalendly, setUpdatingCalendly] = useState(false);
  const [inputValueCalendly, setInputValueCalendly] = useState("");
  const navigate = useNavigate();
  const updateCalendlyMutation = useUpdateCalendlyLink();
  const isSaving = updateCalendlyMutation.isPending;

  const { data: founderDetailsData } = useSuspenseQuery({
    queryKey: queryKeys.dashboards.expert(),
    queryFn: () => getExpertAssesmentStats(),
  });

  const contentArray: IpExpertDashboardRow[] = founderDetailsData.foundersArray.map((h: any) => ({
    ...h,
    daysInStage: h.stageChangedAt ? daysAgo(new Date(h.stageChangedAt)) : "-",
  }));

  // Table shows only assignments that need the advisor's attention.
  const focusRows = contentArray.filter((row) => IPE_FOCUS_STATUSES.has(row.actionItem));

  function saveUpdateCalendlyLink() {
    if (!inputValueCalendly.trim()) return;
    updateCalendlyMutation.mutate(inputValueCalendly, {
      onSuccess: () => {
        setUpdatingCalendly(false);
        setInputValueCalendly("");
      },
    });
  }

  const handleViewMemberDetails = (row: IpExpertDashboardRow) => {
    navigate(`/dashboard/expert-assessments/${row.appId}`);
  };

  /* Counts per status group — shared source of truth with All Assessments, so
     each card's number matches the rows shown when it's clicked through. */
  const groupCounts: Record<string, number> = Object.fromEntries(IPE_STATUS_GROUPS.map((g) => [g.id, 0]));
  for (const row of contentArray) {
    const group = IPE_STATUS_GROUPS.find((g) => g.statuses.includes(row.actionItem));
    if (group) groupCounts[group.id]++;
  }

  const goToFilteredAssessments = (groupId: string) =>
    navigate("/dashboard/all-assessments", { state: { status: `group:${groupId}` } });

  const meetingLinkProps = {
    currentLink: founderDetailsData.link,
    updatingCalendly,
    setUpdatingCalendly,
    inputValueCalendly,
    setInputValueCalendly,
    isSaving,
    onSave: saveUpdateCalendlyLink,
  };

  const isNewExpert = contentArray.length === 0;

  /* ---------------------------------------------------------------- */
  /*  State A: New expert — onboarding layout, no table                */
  /* ---------------------------------------------------------------- */
  if (isNewExpert) {
    return (
      <div className="resourcesCont h-full">
        <h2>Welcome, {founderDetailsData.name}</h2>

        <MeetingLinkCard {...meetingLinkProps} />

        {/* Workflow overview */}
        <Card className="w-full">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-text mb-4">How the assessment process works</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Users, title: "Assigned", desc: "An admin assigns a startup to you for IP assessment" },
                { icon: Clock, title: "Assess", desc: "Meet with the founder, evaluate their IP readiness" },
                { icon: FileCheck, title: "Recommend", desc: "Submit your assessment and tier recommendation" },
              ].map((step, i) => (
                <div key={step.title} className="flex items-start gap-3">
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="h-8 w-8 rounded-lg bg-cSage flex items-center justify-center">
                      <step.icon className="h-4 w-4 text-cGreen" />
                    </div>
                    {i < 2 && <ArrowRight className="h-3.5 w-3.5 text-text/20 hidden sm:block" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text">{step.title}</p>
                    <p className="text-xs text-text/50 mt-0.5">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  State B: Active expert — stats + table                           */
  /* ---------------------------------------------------------------- */
  return (
    <div className="resourcesCont h-full">
      <h2>Welcome Back, {founderDetailsData.name}</h2>

      <MeetingLinkCard {...meetingLinkProps} />

      {/* Summary stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 w-full">
        <StatCard label="IP Assessment Requests" value={groupCounts.requests} icon={AlertTriangle} tier="attention" onClick={() => goToFilteredAssessments("requests")} />
        <StatCard label="IP Assessment In Progress" value={groupCounts.inProgress} icon={Clock} tier="progress" onClick={() => goToFilteredAssessments("inProgress")} />
        <StatCard label="SoWs Awaiting Your Review" value={groupCounts.awaitingReview} icon={FileCheck} tier="urgent" onClick={() => goToFilteredAssessments("awaitingReview")} />
        <StatCard label="Assessment Completed" value={groupCounts.completed} icon={CheckCircle2} tier="info" onClick={() => goToFilteredAssessments("completed")} />
      </div>

      {/* Assignments needing attention */}
      <div className="w-full">
        <DataTable
          columns={columns}
          data={focusRows}
          onRowClick={handleViewMemberDetails}
          enableSorting
          enablePagination
          emptyMessage="You're all caught up — no assignments need your attention right now."
        />
      </div>
    </div>
  );
};

export default IpExpertDashboard;
